import { useEffect, useState } from 'react';
import { Logo } from '../components/Logo';
import { AdminSessionBanner } from '../components/AdminSessionBanner';
import { authedFetch } from '../lib/authed-fetch';
import { redirectToPortalLogin, useCurrentUser } from '../lib/use-current-user';
import '../admin.css';

/**
 * Routing audit log dashboard for canary observability.
 *
 * Mounted at /admin/routing. Authenticated via the portal's Supabase
 * session (cross-subdomain cookie); fetches the gated
 * /api/admin/routing-log endpoint via authedFetch (Authorization:
 * Bearer). Auto-refreshes every 30 seconds. Filter toggles for
 * "fallbacks only" and "errors only".
 *
 * V1 scope: read-only display + filters + auto-refresh. PR #24 added
 * real role-based gating; V2 will add pagination beyond the last 500
 * rows and richer filters.
 */

interface RoutingLogRow {
  id: string;
  timestamp: string;
  email: string | null;
  rule_hit: string | null;
  pool_slug: string | null;
  advisor_name: string | null;
  fallback: boolean;
  fallback_reason: string | null;
  error: string | null;
  ip: string | null;
}

interface ApiResponse {
  ok?: boolean;
  rows?: RoutingLogRow[];
  count?: number;
  error?: string;
  message?: string;
}

const POLL_MS = 30_000;
const TZ = 'America/New_York';

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).format(d);
}

export function AdminRoutingLog() {
  const auth = useCurrentUser();

  const [rows, setRows] = useState<RoutingLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [fallbacksOnly, setFallbacksOnly] = useState(false);
  const [errorsOnly, setErrorsOnly] = useState(false);
  // Lead vs. appointment kind — V5 lead-router routing decisions
  // live alongside slot-first appointment bookings in the same
  // routing_audit_log table. The booking_id column distinguishes
  // them (NULL for lead router, populated for appointment routes).
  const [kind, setKind] = useState<'all' | 'leads' | 'appointments'>('all');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    if (auth.status === 'unauthenticated') {
      window.location.href = redirectToPortalLogin();
    }
  }, [auth.status]);

  useEffect(() => {
    if (auth.status !== 'authenticated' || !auth.user.isAdmin) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let timer: number | null = null;

    async function fetchRows() {
      try {
        const url = new URL('/api/admin/routing-log', window.location.origin);
        if (fallbacksOnly) url.searchParams.set('fallbacks', '1');
        if (errorsOnly) url.searchParams.set('errors', '1');
        if (kind !== 'all') url.searchParams.set('kind', kind);
        const res = await authedFetch(url.toString(), { cache: 'no-store' });
        const json = (await res.json()) as ApiResponse;
        if (cancelled) return;
        if (!res.ok || !json.ok) {
          setErr(json.message ?? json.error ?? `HTTP ${res.status}`);
          setRows([]);
        } else {
          setErr(null);
          setRows(json.rows ?? []);
          setLastUpdated(new Date());
        }
      } catch (e) {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : 'Network error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchRows();
    timer = window.setInterval(fetchRows, POLL_MS);

    return () => {
      cancelled = true;
      if (timer !== null) window.clearInterval(timer);
    };
  }, [auth, fallbacksOnly, errorsOnly, kind]);

  return (
    <div className="admin-app">
      <AdminSessionBanner />
      <header className="admin-header">
        <div className="admin-header-inner">
          <Logo className="admin-logo" />
          <div>
            <h1 className="admin-title">Routing Audit Log</h1>
            <p className="admin-sub">
              Live view of every routing decision · auto-refreshing every 30s
              {lastUpdated && (
                <>
                  {' · last updated '}
                  <strong>{formatTime(lastUpdated.toISOString())}</strong>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="admin-filters">
          <div className="admin-filter-pills" role="tablist" aria-label="Log kind">
            <button
              type="button"
              role="tab"
              className={'admin-filter-pill ' + (kind === 'all' ? 'admin-filter-pill-active' : '')}
              onClick={() => setKind('all')}
              aria-selected={kind === 'all'}
            >
              All
            </button>
            <button
              type="button"
              role="tab"
              className={'admin-filter-pill ' + (kind === 'leads' ? 'admin-filter-pill-active' : '')}
              onClick={() => setKind('leads')}
              aria-selected={kind === 'leads'}
            >
              Lead routing
            </button>
            <button
              type="button"
              role="tab"
              className={'admin-filter-pill ' + (kind === 'appointments' ? 'admin-filter-pill-active' : '')}
              onClick={() => setKind('appointments')}
              aria-selected={kind === 'appointments'}
            >
              Appointment routing
            </button>
          </div>
          <label className="admin-filter">
            <input
              type="checkbox"
              checked={fallbacksOnly}
              onChange={(e) => setFallbacksOnly(e.target.checked)}
            />
            Fallbacks only
          </label>
          <label className="admin-filter">
            <input
              type="checkbox"
              checked={errorsOnly}
              onChange={(e) => setErrorsOnly(e.target.checked)}
            />
            Errors only
          </label>
        </div>
        <nav className="admin-nav">
          <a
            href="/admin/routing"
            className="admin-nav-link admin-nav-link-active"
          >
            Routing Audit Log
          </a>
          <a
            href="/admin/advisors"
            className="admin-nav-link"
          >
            Advisors &amp; Pools
          </a>
          <a
            href="/admin/availability"
            className="admin-nav-link"
          >
            Advisor Availability
          </a>
          <a href="/admin/reminder-opt-outs" className="admin-nav-link">
            Reminder Opt-Outs
          </a>
        </nav>
      </header>

      <main className="admin-main">
        {err && (
          <div className="admin-err" role="alert">
            <strong>Error:</strong> {err}
          </div>
        )}
        {loading && rows.length === 0 && !err && (
          <p className="admin-empty">Loading…</p>
        )}
        {!loading && rows.length === 0 && !err && (
          <p className="admin-empty">
            No routing decisions match the current filters.
          </p>
        )}
        {rows.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Time (ET)</th>
                  <th>Prospect Email</th>
                  <th>Rule</th>
                  <th>Pool</th>
                  <th>Advisor</th>
                  <th>Status</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const status = r.error
                    ? 'error'
                    : r.fallback
                    ? 'fallback'
                    : 'ok';
                  const detail = r.error
                    ? r.error
                    : r.fallback_reason
                    ? r.fallback_reason
                    : '';
                  return (
                    <tr key={r.id} className={`admin-row admin-row-${status}`}>
                      <td className="admin-cell-time">{formatTime(r.timestamp)}</td>
                      <td>{r.email ?? '—'}</td>
                      <td><code>{r.rule_hit ?? '—'}</code></td>
                      <td>{r.pool_slug ?? '—'}</td>
                      <td>{r.advisor_name ?? '—'}</td>
                      <td>
                        <span className={`admin-badge admin-badge-${status}`}>
                          {status}
                        </span>
                      </td>
                      <td className="admin-cell-detail">{detail}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
